// Character limits enforced on both the HTML input (maxLength) and in validation logic
export const LIMITS = {
  name: 100,
  email: 254,
  phone: 20,
  school: 150,
  message: 2000,
  address: 500,
  notes: 1000,
}

// Remove all HTML/XML tags from a string before sending to the API
export function stripTags(str) {
  return typeof str === 'string' ? str.replace(/<[^>]*>/g, '').trim() : ''
}

// Detect XSS and SQL injection sequences.
// Rejects script tags, javascript: URLs, event handlers, and unambiguous SQL injection
// sequences like '; DROP TABLE or UNION SELECT.
function hasDangerousContent(str) {
  const xss = /<\s*(script|iframe|object|embed|img|form|link|meta|svg)[^>]*>|javascript\s*:|data\s*:\s*text\/html|on\w+\s*=|<\/\s*(script|iframe|object|embed)/gi
  const sql = /(')\s*(?:--|;|\/\*)|\bUNION\s+(?:ALL\s+)?SELECT\b|;\s*(?:DROP|DELETE|TRUNCATE|ALTER|INSERT|UPDATE|CREATE|EXEC)\b/gi
  return xss.test(str) || sql.test(str)
}

export function validateEmail(v) {
  const val = (v || '').trim()
  if (!val) return 'Email is required'
  if (val.length > LIMITS.email) return `Email must be under ${LIMITS.email} characters`
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val)) return 'Enter a valid email address'
  if (hasDangerousContent(val)) return 'Email contains invalid content'
  return null
}

export function validateName(v, label = 'Name', required = true) {
  const val = (v || '').trim()
  if (!val) return required ? `${label} is required` : null
  if (val.length > LIMITS.name) return `${label} must be under ${LIMITS.name} characters`
  if (hasDangerousContent(val)) return `${label} contains invalid content`
  if (!/^[a-zA-Z0-9 '\-.,()&/]+$/.test(val)) return `${label} contains invalid characters`
  return null
}

export function validateSchoolName(v, label = 'School Name') {
  const val = (v || '').trim()
  if (!val) return `${label} is required`
  if (val.length > LIMITS.school) return `${label} must be under ${LIMITS.school} characters`
  if (hasDangerousContent(val)) return `${label} contains invalid content`
  // School names allow slightly broader characters
  if (!/^[a-zA-Z0-9 '\-.,()&/']+$/.test(val)) return `${label} contains invalid characters`
  return null
}

export function validatePhone(v) {
  const val = (v || '').trim()
  if (!val) return null // optional in most forms
  if (val.length > LIMITS.phone) return `Phone must be under ${LIMITS.phone} characters`
  if (!/^[+0-9\s\-()]+$/.test(val)) return 'Enter a valid phone number (digits, +, spaces only)'
  return null
}

export function validateFreeText(v, label, required = false, limit = LIMITS.message) {
  const val = (v || '').trim()
  if (required && !val) return `${label} is required`
  if (val.length > limit) return `${label} must be under ${limit} characters`
  if (hasDangerousContent(val)) return `${label} contains invalid content`
  return null
}

export function validateQuantity(v, min = 1, max = 50) {
  const n = Number(v)
  if (!Number.isInteger(n) || n < min || n > max) {
    return `Quantity must be a whole number between ${min} and ${max}`
  }
  return null
}

// Sanitize a free-text value before sending — strips HTML tags, trims whitespace
export function sanitize(str) {
  return stripTags(str || '').trim()
}
